import { InfoNote } from '../../components/InfoNote';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SkeletonCard } from '../../components/SkeletonCard';
import { TimeslotChipSelector } from '../../components/TimeslotChipSelector';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import { PreferencesPanel } from '../PreferencesPanel';
import { ReservationPanel } from '../ReservationPanel';
import {
  ScrollContainer,
  SkeletonContainer,
  TimeslotDivider,
} from './SpaTreatmentFacilityBookingContent.styles';
import type { SpaTreatmentFacilityBookingContentProps } from './SpaTreatmentFacilityBookingContent.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function SpaTreatmentFacilityBookingContent({
  title,
  subtitle,
  isLoading = false,
  guestCount,
  minGuests,
  maxGuests,
  onGuestCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  calendarLabels,
  timeslots,
  selectedTimeslot,
  onTimeslotSelect,
  insufficientAvailability = false,
  noteText,
  noteIcon = defaultNoteIcon,
  guests,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  genderOptions,
  preferencesEnabled,
  onPreferencesToggle,
  preferenceCategories,
  expandedPreferences,
  onPreferenceCategoryToggle,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  labels,
}: SpaTreatmentFacilityBookingContentProps) {
  const selectedGuestIds = guests
    .map((g) => g.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  return (
    <ScrollContainer>
      <PageHeading title={title} subtitle={subtitle} />

      <PanelCard>
        <ReservationPanel
          title={labels?.reservation?.title}
          subtitle={labels?.reservation?.subtitle}
          guestLabel={labels?.reservation?.guestLabel}
          guestCount={guestCount}
          minGuests={minGuests}
          maxGuests={maxGuests}
          onGuestCountChange={onGuestCountChange}
          activeDate={activeDate}
          selectedDate={selectedDate}
          calendarView={calendarView}
          availability={availability}
          onDateSelect={onDateSelect}
          onWeekNavigate={onWeekNavigate}
          onCalendarViewChange={onCalendarViewChange}
          calendarLabels={calendarLabels}
          counterDecreaseLabel={labels?.counter?.decrease}
          counterIncreaseLabel={labels?.counter?.increase}
        />
        <TimeslotDivider />
        <TimeslotChipSelector
          timeslots={timeslots}
          selectedTimeslot={selectedTimeslot}
          onTimeslotSelect={onTimeslotSelect}
          labels={labels?.timeslotChips}
        />
        {insufficientAvailability && labels?.insufficientAvailabilityText && (
          <InfoNote text={labels.insufficientAvailabilityText} variant="warning" />
        )}
        {noteText && <InfoNote text={noteText} icon={noteIcon} />}
      </PanelCard>

      {isLoading ? (
        Array.from({ length: guestCount }, (_, i) => (
          <SkeletonContainer key={i}>
            <SkeletonCard variant="simple" />
          </SkeletonContainer>
        ))
      ) : (
        <>
          {guests.map((guest, guestIndex) => {
            const disabledGuestIds = selectedGuestIds.filter((id) => id !== guest.selectedGuestId);

            return (
              <PanelCard key={guest.guestNumber}>
                <GuestDetailsPanel
                  guestNumber={guest.guestNumber}
                  existingGuests={guest.existingGuests}
                  selectedGuestId={guest.selectedGuestId}
                  disabledGuestIds={disabledGuestIds}
                  onExistingGuestSelect={(guestId) => onExistingGuestSelect(guestIndex, guestId)}
                  values={guest.values}
                  onChange={(field, value) => onGuestChange(guestIndex, field, value)}
                  titleOptions={titleOptions}
                  genderOptions={genderOptions}
                  errors={guest.errors}
                  labels={labels?.guestDetails}
                />
              </PanelCard>
            );
          })}

          <PanelCard>
            <PreferencesPanel
              title={labels?.preferences?.title}
              enabled={preferencesEnabled}
              onToggle={onPreferencesToggle}
              categories={preferenceCategories}
              expandedCategories={expandedPreferences}
              onCategoryToggle={onPreferenceCategoryToggle}
              onOptionToggle={onPreferenceOptionToggle}
              onOtherChange={onPreferenceOtherChange}
              onFreeTextChange={onPreferenceFreeTextChange}
              otherButtonLabel={labels?.preferences?.otherButtonLabel}
            />
          </PanelCard>
        </>
      )}
    </ScrollContainer>
  );
}
